import * as Radix from "@radix-ui/themes";
import * as RemixReact from "@remix-run/react";
import * as React from "react";

export type LinkProps = Pick<
  RemixReact.LinkProps,
  | "to"
  | "onClick"
  | "children"
  | "className"
  | "prefetch"
  | "reloadDocument"
  | "id"
  | "target"
  | "rel"
>;

export type FormProps = Pick<
  RemixReact.FormProps,
  "id" | "action" | "children" | "method" | "className" | "onSubmit"
>;

export type ReactRouterStubs = {
  Link: React.ComponentType<LinkProps>;
  Form: React.ComponentType<FormProps>;
  navigate: RemixReact.NavigateFunction;
  navigation: RemixReact.Navigation;
  location: RemixReact.Location;
  searchParams: [
    URLSearchParams,
    (update: (params: URLSearchParams) => URLSearchParams) => void,
  ];
  routeError: unknown;
  revalidator: ReturnType<typeof RemixReact.useRevalidator>;
};

export const useUpstreamReactRouterStubs = (): ReactRouterStubs => {
  return {
    Link: RemixReact.Link,
    Form: RemixReact.Form,
    navigate: RemixReact.useNavigate(),
    navigation: RemixReact.useNavigation(),
    location: RemixReact.useLocation(),
    searchParams: RemixReact.useSearchParams(),
    routeError: RemixReact.useRouteError(),
    revalidator: RemixReact.useRevalidator(),
  };
};

const VanillaJsLink: React.FC<LinkProps> = (props) => {
  return (
    <Radix.Link
      id={props.id}
      className={props.className}
      onClick={props.onClick}
      href={props.to?.toString()}
      target={props.target}
      rel={props.rel}
    >
      {props.children}
    </Radix.Link>
  );
};

const VanillaJsForm: React.FC<FormProps> = (props) => {
  return (
    <form
      id={props.id}
      method={props.method}
      action={props.action}
      className={props.className}
      onSubmit={props.onSubmit}
    >
      {props.children}
    </form>
  );
};

const vanillaJsNavigate: RemixReact.NavigateFunction = (url) => {
  window.document.location = url.toString();
};

const vanillaJsNavigation: RemixReact.Navigation = {
  state: "idle",
  location: undefined,
  json: undefined,
  text: undefined,
  formData: undefined,
  formAction: undefined,
  formMethod: undefined,
  formEncType: undefined,
};

export const useVanillaJsReactRouterStubs = (): ReactRouterStubs => {
  const location: RemixReact.Location = React.useMemo(
    () => ({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      key: "",
      state: undefined,
    }),
    [],
  );

  const { searchParams, setSearchParams } = React.useMemo(() => {
    const url = new URL(window.document.URL);

    return {
      searchParams: url.searchParams,
      setSearchParams: (f: (params: URLSearchParams) => void) => {
        f(url.searchParams);
        window.history.replaceState(undefined, "", url);
      },
    };
  }, []);

  const revalidator: ReactRouterStubs["revalidator"] = React.useMemo(
    () => ({
      state: "idle",
      revalidate: () => {
        console.log("Revalidate was called, I want to refresh the page plz");
        window.location.reload();
      },
    }),
    [],
  );

  return {
    Link: VanillaJsLink,
    Form: VanillaJsForm,
    navigate: vanillaJsNavigate,
    navigation: vanillaJsNavigation,
    location,
    searchParams: [searchParams, setSearchParams],
    routeError: undefined,
    revalidator,
  };
};

export const getMockedReactRouterStubs = (): ReactRouterStubs => {
  const location: RemixReact.Location = {
    pathname: "/test/path",
    search: "",
    hash: "",
    key: "",
    state: undefined,
  };

  const url = new URL(window.document.URL);

  const searchParams = url.searchParams;
  const setSearchParams = (f: (params: URLSearchParams) => void) => {
    f(url.searchParams);
    window.history.replaceState(undefined, "", url);
  };

  const revalidator: ReactRouterStubs["revalidator"] = {
    state: "idle",
    revalidate: () => {
      console.log("Revalidate was called, I want to refresh the page plz");
      window.location.reload();
    },
  };

  return {
    Link: VanillaJsLink,
    Form: VanillaJsForm,
    navigate: vanillaJsNavigate,
    navigation: vanillaJsNavigation,
    location,
    searchParams: [searchParams, setSearchParams],
    routeError: undefined,
    revalidator,
  };
};

export const ReactRouterStubsContext = React.createContext<ReactRouterStubs>({
  Link: VanillaJsLink,
  Form: VanillaJsForm,
  navigate: vanillaJsNavigate,
  navigation: vanillaJsNavigation,
  location: {
    pathname: "/",
    search: "",
    hash: "",
    key: "",
    state: undefined,
  },
  searchParams: [new URLSearchParams(), () => {}],
  routeError: undefined,
  revalidator: {
    state: "idle",
    revalidate: () => {
      window?.location.reload();
    },
  },
});
